import {
    AccountDataType,
    accountSourceDataTypeChecker,
    AccountSourceType,
    addAccount,
    Characteristics,
    getAccountBySourceId,
    LoadState,
    M365AuthProvider,
    M365ConnectedMailboxAccountSource,
    M365Mailbox,
    M365MailboxType,
    M365UserMailboxAccountSource,
    updateAccount,
} from 'owa-account-source-list-store';
import type { MailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyEnsureAdditionalAccountInfoData } from '../lazyIndex';
import { HostSourceInformation, SourceAuthType } from '../types';
import { buildSourceId, globalProfile, microsoft365SourceType } from './sourceIdBuilder';

/**
 * Returns the MailboxInfo for the specified sourceId
 * @param sourceId the sourceId for the account
 * @returns The mailbox information, or undefined if the account is not a mailbox
 */
export function getMailboxBySourceId(sourceId: string): MailboxInfo | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    return accountSourceDataTypeChecker.isM365Mailbox(sourceInfo)
        ? {
              sourceId,
              type: sourceInfo.m365mailbox.type,
              userIdentity: sourceInfo.m365mailbox.userIdentity,
              mailboxSmtpAddress: sourceInfo.m365mailbox.emailAddress,
          }
        : undefined;
}

export function getMailboxTypeBySourceId(sourceId: string): string | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    switch (sourceInfo?.sourceType) {
        case AccountSourceType.Office365:
            return 'OFFICE365';

        case AccountSourceType.OutlookDotCom:
            return 'OUTLOOKDOTCOM';

        default:
            return undefined;
    }
}

export function buildMailboxCharacteristics(hostAccount: HostSourceInformation): Characteristics {
    return {
        ...hostAccount.characteristics,
        isMicrosoft365Hosted: true,
    };
}

export function buildM365UserMailbox(hostAccount: HostSourceInformation): M365Mailbox {
    return hostAccount.mailbox
        ? {
              ...hostAccount.mailbox,
              type: 'UserMailbox',
              logonEmailAddress: hostAccount.mailbox.loginAddress,
              authProviderType:
                  hostAccount.authProviderType === SourceAuthType.Microsoft365
                      ? M365AuthProvider.AAD
                      : M365AuthProvider.MSA,
          }
        : {
              type: 'UserMailbox',
              name: '',
              displayName: '',
              emailAddress: '',
              logonEmailAddress: '',
              userIdentity: '',
              routingHint: '',
              authProviderType: M365AuthProvider.AAD,
              alternateId: '',
              accountUniqueId: '',
              imageUrl: '',
              profileUrl: '',
          };
}

/**
 * Converts from HostSourceInformation to M365UserMailboxAccountSource
 * @param sourceId SourceId to be used for the host source
 * @param hostAccount Host account to be update
 * @returns The AccountSource information
 */
function toM365UserMailboxAccountSource(
    sourceId: string,
    hostAccount: HostSourceInformation
): M365UserMailboxAccountSource {
    const characteristics = buildMailboxCharacteristics(hostAccount);
    const m365mailbox = buildM365UserMailbox(hostAccount);

    return {
        sourceId: sourceId,
        sourceType:
            hostAccount.authProviderType === SourceAuthType.Microsoft365
                ? AccountSourceType.Office365
                : AccountSourceType.OutlookDotCom,
        displayName: hostAccount.displayName,
        characteristics: characteristics,
        externalData: hostAccount.externalData,
        m365mailbox: m365mailbox,
        dataType: AccountDataType.M365Mailbox,
        m365mailboxType: M365MailboxType.User,
        groupSources: {
            loadState: LoadState.NotStarted,
            hostingSourceId: sourceId,
            sources: [],
        },
        sharedSources: {
            loadState: LoadState.NotStarted,
            hostingSourceId: sourceId,
            sources: [],
        },
        mailboxInfo: {
            type: 'UserMailbox',
            userIdentity: m365mailbox.userIdentity,
            mailboxSmtpAddress: m365mailbox.emailAddress,
            sourceId,
        },
        archiveSources: {
            loadState: LoadState.NotStarted,
            hostingSourceId: sourceId,
            sources: [],
        },
    };
}

function toM365ConnectedMailboxAccountSource(
    sourceId: string,
    hostAccount: HostSourceInformation
): M365ConnectedMailboxAccountSource {
    const characteristics = buildMailboxCharacteristics(hostAccount);
    const m365mailbox = buildM365UserMailbox(hostAccount);

    return {
        sourceId: sourceId,
        sourceType:
            hostAccount.authProviderType === SourceAuthType.Microsoft365
                ? AccountSourceType.Office365
                : AccountSourceType.OutlookDotCom,
        displayName: hostAccount.displayName,
        characteristics: characteristics,
        externalData: hostAccount.externalData,
        m365mailbox: m365mailbox,
        dataType: AccountDataType.M365Mailbox,
        m365mailboxType: M365MailboxType.Connected,
        accountState: hostAccount.accountState ?? {
            connectedAccountState: 0,
        },
        mailboxInfo: {
            type: 'UserMailbox',
            userIdentity: m365mailbox.userIdentity,
            mailboxSmtpAddress: m365mailbox.emailAddress,
            sourceId,
        },
    };
}

/**
 * convert the given account (HostSourceInformation) to M365UserMailboxAccountSource
 * and adds to the source list store, and returns the sourceId
 * @param account
 */
export function addM365UserMailboxHostAccount(account: HostSourceInformation): string {
    if (!account.mailbox) {
        throw new Error('Can not create a Connected Mailbox for an account that has no mailbox');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox.userIdentity
    );

    if (!getAccountBySourceId(sourceId)) {
        const accountSource = toM365UserMailboxAccountSource(sourceId, account);
        addAccount(accountSource);
    }

    return sourceId;
}

/**
 * convert the given account (HostSourceInformation) to M365UserMailboxHostAccountSource
 * and update the source list store with the converted account, and returns the sourceId
 * @param account
 */
export function updateM365UserMailboxHostAccount(account: HostSourceInformation): string {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox!.userIdentity
    );

    if (getAccountBySourceId(sourceId)) {
        updateAccount(toM365UserMailboxAccountSource(sourceId, account));
        const updatedAccount = getAccountBySourceId(sourceId);

        if (isFeatureEnabled('acct-sourceIdAwareGroupsStore')) {
            lazyEnsureAdditionalAccountInfoData.importAndExecute(
                updatedAccount!,
                true /** forceUpdate */
            );
        }
    }

    return sourceId;
}

/**
 * convert the given account (HostSourceInformation) to M365ConnectedMailboxAccountSource
 * and adds to the source list store, and returns the sourceId
 * @param account
 */
export function addM365ConnectedMailboxHostAccount(account: HostSourceInformation): string {
    if (!account.mailbox) {
        throw new Error('Can not create a Connected Mailbox for an account that has no mailbox');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox.userIdentity
    );

    if (!getAccountBySourceId(sourceId)) {
        const accountSource = toM365ConnectedMailboxAccountSource(sourceId, account);
        addAccount(accountSource);
    }

    return sourceId;
}
